import React from "react";
import styles from "./RebateTreeTable.module.css"; // adjust path/name if different
import ProgressBarCell, { TierDetail } from "../progress-bar"; // adjust path if different

// Shape of the raw rebate data (dummyDataForRebate)
type RebateRow = {
    type: "Parents" | "Others";
    district: string;
    supplier: string;
    commodity: string;
    category: string;
    growthTierType: string;
    backToZero: string;
    dollarPoundToNextTier: number;
    currentPurchase: number;
    currentRebatePurchases: string | number;
    nextTierBenefit: number;
    projectedPurchaseEnd: number;
    netSpend?: number;
    tierDetails: TierDetail[];
};

export type TreeNode = {
    id: string;
    type: "Parents" | "Others";
    district: string;
    supplier: string;
    commodity: string;
    category: string;
    growthTierType: string;
    backToZero: string;
    dollarPoundToNextTier: number;
    nextTierBenefit: number;
    projectedPurchaseEnd: number;
    currentPurchase: number;
    currentRebatePurchases: string | number;
    tierDetails: TierDetail[];
    progress?: number;
    netSpend?: number;
    children?: TreeNode[];
};

type FlatTreeNode = TreeNode & {
    depth: number;
    parentId?: string;
};

type CustomTreeTableProps = {
    data: TreeNode[];
    columns: { key: string; label: string; width?: string }[];
    onRowClick?: (row: FlatTreeNode | null) => void;
    value?: string | null; // selected row id (optional)
    height?: string | null
};

const clamp = (n: number, min: number, max: number) =>
    Math.max(min, Math.min(max, n));

const isBaseTierType = (tierType: string) => {
    const normalized = tierType?.trim().toLowerCase();
    return normalized === "base tier" || normalized === "base rebate";
};

const getParentFilledBoxes = (tiers: TierDetail[]) => {
    const boxCount = Math.max(tiers.length - 1, 0);
    if (!boxCount) return 0;

    const progressSum = tiers.reduce((sum, tier) => {
        if (isBaseTierType(tier.tierType)) return sum;
        return sum + (Number(tier.progress) || 0);
    }, 0);

    if (progressSum <= 0) return 0;

    return clamp(Math.round(progressSum), 0, boxCount);
};

const sortTierDetails = (tiers: TierDetail[] = []): TierDetail[] => {
    const orderOf = (t: TierDetail) => {
        const name = (t.tierType || "").toUpperCase();
        if (name.includes("BASE")) return 0;
        const m = name.match(/TIER\s*(\d+)/);
        if (m && m[1]) return parseInt(m[1], 10) || 999;
        return 999;
    };

    // sort first
    const sorted = [...tiers].sort((a, b) => orderOf(a) - orderOf(b));

    // then remove duplicate tier names (case-insensitive), keeping first
    const seen = new Set<string>();
    return sorted.filter((t) => {
        const key = (t.tierType || "").toUpperCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

// transform raw rebate data (with type = "Parent" | "Others") into a tree
export function transformRebateDataToTreeTable(data: RebateRow[]): TreeNode[] {
    const result: TreeNode[] = [];

    // group by district
    const districtMap: Record<string, RebateRow[]> = {};
    data.forEach((item) => {
        if (!districtMap[item.district]) districtMap[item.district] = [];
        districtMap[item.district].push({
            ...item,
            tierDetails: sortTierDetails(item.tierDetails), // ensure tier order once here
        });
    });

    Object.entries(districtMap).forEach(([district, items]) => {
        const parents = items.filter((i) => i.type === "Parents");
        const others = items.filter((i) => i.type === "Others");

        // track "Others" rows that have been attached to a parent
        const attachedOthers = new Set<number>();

        parents.forEach((parent, pIdx) => {
            const children: TreeNode[] = [];
            const currentFilledBox = Number(parent?.tierDetails?.[getParentFilledBoxes(parent.tierDetails)]?.benefitPercentage)

            others.forEach((child, cIdx) => {

                if (
                    child.supplier === parent.supplier &&
                    child.district === parent.district
                ) {

                    attachedOthers.add(cIdx);
                    children.push({
                        ...child,
                        tierDetails: sortTierDetails(child.tierDetails),
                        id: `${district}-${child.supplier}-${child.commodity}-${child.category}-${cIdx}`,
                        currentRebatePurchases: (child.currentPurchase * currentFilledBox) / 100,
                        progress: undefined,
                        children: undefined,
                    });
                }
            });

            // parent row
            result.push({
                ...parent,
                tierDetails: sortTierDetails(parent.tierDetails),
                id: `${district}-${parent.supplier}-parent-${pIdx}`,
                progress: undefined,
                children,
                category: "All",
                commodity: "All"
            });
        });

        // "Others" without a matching parent stay flat
        others.forEach((item, idx) => {
            if (attachedOthers.has(idx)) return;
            result.push({
                ...item,
                tierDetails: sortTierDetails(item.tierDetails),
                id: `${district}-${item.supplier}-${item.commodity}-${item.category}-${idx}`,
                progress: undefined,
                children: undefined,
            });
        });
    });

    return result;
}

export const rebateTreeTableColumns = [
    { key: "district", label: "District", width: "11%" },
    { key: "supplier", label: "Supplier", width: "14%" },
    { key: "commodity", label: "Commodity", width: "12%" },
    { key: "category", label: "Category", width: "15%" },
    { key: "progress", label: "Progress", width: "48%" },
];

const flattenTree = (
    nodes: TreeNode[],
    expanded: Set<string>
): FlatTreeNode[] => {
    const result: FlatTreeNode[] = [];

    const visit = (node: TreeNode, depth: number, parentId?: string) => {
        const flatNode: FlatTreeNode = {
            ...node,
            depth,
            parentId,
        };
        result.push(flatNode);

        if (
            node.children &&
            node.children.length > 0 &&
            expanded.has(node.id)
        ) {
            node.children.forEach((child) => visit(child, depth + 1, node.id));
        }
    };

    nodes.forEach((n) => visit(n, 0));
    return result;
};

const RebateTreeTable: React.FC<CustomTreeTableProps> = ({
    data,
    columns,
    onRowClick,
    value,
    height
}) => {
    const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
    const [selectedRow, setSelectedRow] = React.useState<FlatTreeNode | null>(
        null
    );

    const flatRows = React.useMemo(
        () => flattenTree(data, expanded),
        [data, expanded]
    );

    const rowsByDistrict = React.useMemo(() => {
        const map: Record<string, FlatTreeNode[]> = {};
        flatRows.forEach((row) => {
            if (!map[row.district]) map[row.district] = [];
            map[row.district].push(row);
        });
        return map;
    }, [flatRows]);

    const districts = React.useMemo(
        () => Object.keys(rowsByDistrict).sort((a, b) => a.localeCompare(b)),
        [rowsByDistrict]
    );

    const handleRowClick = (row: FlatTreeNode | null) => {
        setSelectedRow(row);
        if (onRowClick) onRowClick(row);
    };

    // keep selected row in sync with external value (if provided)
    React.useEffect(() => {
        if (!value) return;
        const found = flatRows.find((r) => r.id === value);
        if (found) setSelectedRow(found);
    }, [value, flatRows]);

    return (
        <div style={{ height: height ? height : "100%" }}>
            <table className={styles.tableRoot}>
                <thead className={styles.tableHeader}>
                    <tr>
                        {columns.map((col) => (
                            <th
                                key={col.key}
                                style={{ width: col.width }}
                                className={styles.headerCell}
                            >
                                {col.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {districts.map((district, districtIdx) => {
                        const rows = rowsByDistrict[district];
                        let districtRendered = false;
                        const zebraClass =
                            districtIdx % 2 === 0
                                ? styles.districtGroupEven
                                : styles.districtGroupOdd;

                        return (
                            <React.Fragment key={district}>
                                {rows.map((row, rowIndx) => {
                                    const isParent =
                                        !!row.children &&
                                        row.children.length > 0;
                                    const isChild =
                                        row.parentId !== undefined;
                                    const showDistrict = !districtRendered;
                                    if (showDistrict) districtRendered = true;

                                    const rowClass =
                                        styles.tableRow +
                                        " " +
                                        styles.hoverableRow;

                                    // const zebraClass =
                                    //     rowIndx % 2 === 0
                                    //         ? styles.districtGroupEven
                                    //         : styles.districtGroupOdd;

                                    return (
                                        <tr
                                            key={row.id}
                                            aria-label={
                                                expanded.has(row.id)
                                                    ? "Collapse"
                                                    : "Expand"
                                            }
                                            onClick={
                                                row.type === "Parents"
                                                    ? () => {
                                                        handleRowClick(null)
                                                        setExpanded(
                                                            (prev) => {
                                                                const next =
                                                                    new Set(
                                                                        prev
                                                                    );
                                                                if (
                                                                    next.has(
                                                                        row.id
                                                                    )
                                                                )
                                                                    next.delete(
                                                                        row.id
                                                                    );
                                                                else
                                                                    next.add(
                                                                        row.id
                                                                    );
                                                                return next;
                                                            }
                                                        )
                                                    }

                                                    : () =>
                                                        handleRowClick(row)
                                            }
                                            className={`${rowClass} ${zebraClass} ${selectedRow &&
                                                selectedRow.id === row.id &&
                                                styles.activeRow
                                                }`}
                                        >
                                            {showDistrict && (
                                                <td
                                                    rowSpan={rows.length}
                                                    className={
                                                        styles.districtCell
                                                    }
                                                >
                                                    {row.district}
                                                </td>
                                            )}
                                            <td className={styles.supplierCell}>
                                                {isParent ? (
                                                    <button
                                                        type="button"
                                                        className={
                                                            styles.expandButton
                                                        }
                                                    >
                                                        {expanded.has(row.id)
                                                            ? "−"
                                                            : "+"}
                                                    </button>
                                                ) : isChild ? (
                                                    <span
                                                        className={
                                                            styles.childIndent
                                                        }
                                                    />
                                                ) : null}
                                                {row.supplier}
                                            </td>
                                            <td
                                                className={
                                                    styles.commodityCell
                                                }
                                            >
                                                {isParent
                                                    ? "All"
                                                    : row.commodity}
                                            </td>
                                            <td
                                                className={
                                                    styles.categoryCell
                                                }
                                            >
                                                {isParent
                                                    ? "All"
                                                    : row.category}
                                            </td>
                                            <td
                                                className={
                                                    styles.progressCell
                                                }
                                            >
                                                {/* ProgressBarCell decides layout based on row.type ("Parent" | "Others") */}
                                                <ProgressBarCell row={row} />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div >
    );
};

export default RebateTreeTable;